import React from 'react';
import { withRouter } from 'react-router';
import ApplicationActions from '../actions/ApplicationActions.js';
import IDVerificationActions from '../actions/IDVerificationActions.js';
import IDVerificationStore from '../stores/IDVerificationStore';
import ApplicationStore from '../stores/ApplicationStore';
import ClassNames from 'classnames';
import autoBind from 'react-autobind';
import {ProductIds} from './../constants/ApplicationConstants';
import {ConfirmButtonLabels} from './../constants/IDVerificationConstants';
import NavigationBar from './NavigationBar';
import Individual from './IDVerification/Individual';
import ContentService from '../services/ContentService';

import SectionHeader from './Common/SectionHeader.jsx';
import InlineInfoMessage from './Common/InlineInfoMessage.jsx';
import InlineInfoButton from './Common/InlineInfoButton.jsx';

import Footer from './Footer.jsx';
import FooterUtil from './../utils/FooterHelper';
import Transitions from './../core/Transitions';
import DownloadFormsHelper from '../utils/DownloadFormsHelper.js';
import OmnitureTracking from '../utils/OmnitureTracking.js';

function getIDVerificationState() {
    return {
        content: ContentService.getContent().idVerification,
        footerContent: ContentService.getContent().footer,
        moreMenuContent: ContentService.getContent().moreMenu,
        downloadMenuContent:ContentService.getContent().downloadMenu,
        product: IDVerificationStore.getProduct(),
        showMoreMenu: IDVerificationStore.getShowHideMore(),
        options: IDVerificationStore.getOptions(),
        hasAllRequirements: IDVerificationStore.getAllValidations(),
        formModel: IDVerificationStore.getFormModel(),
        investorList: IDVerificationStore.getInvestorList(),
        idVerificationBackFile: IDVerificationStore.getIdVerificationBackFile(),
        toggleAccreditedEnglishHelp: true,
        savingForm: false,
        submitDocument: false,
        idVerificationModel: IDVerificationStore.getIDVerificationModel(),
        accountNumber: IDVerificationStore.getAccountNumber(),
        hasSubmitFailed: IDVerificationStore.getHasSubmitFailed(),
        showDownloadMenu: IDVerificationStore.getShowDownloadMenu(),
        formsToDownload: IDVerificationStore.getFormsToDownload(),
        enableDownloadButton: IDVerificationStore.getEnableDownloadButton(),
        accountType: IDVerificationStore.getAccountType(),
        downloadInProgress: IDVerificationStore.getDownloadInProgress(),
        selectedForms: IDVerificationStore.getSelectedForms(),
        readyForDownload: IDVerificationStore.getReadyForDownload(),
        downloadedForms: IDVerificationStore.getDownloadedForms(),
        formDownloaded:IDVerificationStore.getIsFormsDownloaded(),
        downloadFormsFailed: IDVerificationStore.getDownloadFormsFailed(),
        clientIdentificationMode: ApplicationStore.getClientIdentificationMode()
    };
}

class IDVerification extends React.Component {
    constructor(props) {
        super(props);
        autoBind(this);
        this.state = getIDVerificationState();
    }

    componentDidMount() {
        IDVerificationStore.addChangeListener(this._onChange);
        OmnitureTracking.getTrackingEnabled();
    }

    componentWillUnmount() {
        IDVerificationStore.removeChangeListener(this._onChange);
    }

    _onChange() {
        this.setState(getIDVerificationState());
    }

    getUserIdVerification(investorList) {
        let allInvestors = [];

        _.each(investorList, (investor, idx) => {
            if (!investor.kycFlag) {
                const data = {
                    investorId: investor.investorId,
                    kycFlag: investor.kycFlag,
                    fullName: investor.fullName,
                    oin: investor.oin,
                    verificationDocuments: investor.verificationDocuments
                };

                let uniqueKey = 'idverification-individual-' + idx;
                allInvestors.push(<Individual key={uniqueKey} options={this.state.options[idx]} 
                        hasAllRequirements={this.state.hasAllRequirements} data={data} 
                        updateVerificationDocument={this.updateVerificationDocument.bind(this)}/>);
            }
        });
        return allInvestors;
    }
    
    agreeIdVerificationForm() {
        ApplicationActions.agreeIDVerificationForm(this.state.idVerificationModel);
        this.setState({savingForm: true});
    }

    updateVerificationDocument() {
        ApplicationActions.updateVerificationDocument(this.state.idVerificationModel);
    }

    componentWillUpdate(nextProps, nextState) {
        if (nextState.submitDocument && nextState.hasAllRequirements) {
            Transitions.forwardFromIDVerification(this.props.router, {submitDocument: (nextState.hasAllRequirements && nextState.submitDocument)});
        }

        if (!this.state.hasSubmitFailed && nextState.hasSubmitFailed){
            Transitions.forwardFromIDVerification(this.props.router, {submitFailed: true, backButton: true});
        }else if (!this.state.accountNumber && nextState.accountNumber) {
            Transitions.forwardFromIDVerification(this.props.router);
        }

        let isBackFileChanged = nextState.idVerificationBackFile !== null &&
            !_.isEqual(this.state.idVerificationBackFile, nextState.idVerificationBackFile);
        let isSubmitReset = this.state.hasSubmitFailed && !nextState.hasSubmitFailed;
        if (nextState.savingForm && (isBackFileChanged || isSubmitReset || nextState.hasAllRequirements)) {
            // boardcast work item model to update formModel of corresponding stores
            IDVerificationActions.updateWorkItemModel({
                idVerificationBackFile: nextState.idVerificationBackFile
            });
            this.setState({savingForm: false});
        }

        if (this.state.savingForm && !nextState.savingForm) {
            // form saved and work item model was updated, now can submit document
            this.setState({ submitDocument: true });
        }
    }
    
    getNotificationStatus(canProceedAgree, addedAtLeastOneDoc) {
        if (canProceedAgree) {
            return (<div className="row" key="notification-alldocs">
                        <div className="digital-consent-form__notification col-xs-12">
                            <div className="alert alert-success" role="alert"><span className="glyphicon glyphicon-ok" />
                                You have now provided all the required documents, please read and agree to the declaration below.
                            </div>
                        </div>
                    </div>);
        } else if (addedAtLeastOneDoc) {
            return (<div className="row" key="notification-requiremoredocs">
                        <div className="digital-consent-form__notification col-xs-12">
                            <div className="alert alert-info" role="alert">
                                <strong>Note: </strong>We still require a few more documents from you.
                            </div>
                        </div>
                    </div>);
        } 
        return null;
    }

    toggleAccreditedEnglishHelp() {
        this.setState({
            toggleAccreditedEnglishHelp: !this.state.toggleAccreditedEnglishHelp
        });
    }

    goBackFromIDVerification() {
        Transitions.forwardFromIDVerification(this.props.router, {backButton:true});
    }

    skipToConfirmDetails() {
        Transitions.forwardFromIDVerification(this.props.router, {skipToConfirmDetails:true});
    }

    getSkipOrDisagreeButton() {
        if (this.state.product.value === ProductIds.SUPER) {
            return (<button type="button" onClick={this.skipToConfirmDetails.bind(this)} 
                        className="btn btn-default btn-large digital-consent-form__disagreeBtn">
                        <span className="glyphicon glyphicon-menu-right pull-right" /> 
                        {ConfirmButtonLabels.SKIP}
                    </button>);
        } else {
            return (<button type="button" onClick={this.skipToConfirmDetails.bind(this)} 
                        className="btn btn-default btn-large digital-consent-form__disagreeBtn" >
                        <span className="glyphicon glyphicon-menu-right pull-right" /> 
                        {ConfirmButtonLabels.DISAGREE}
                    </button>);
        }
    }

    render() {
        let userIdVerification = this.getUserIdVerification(this.state.investorList);
        
        let addedAtLeastOneDoc = _.some(this.state.investorList, function(investor) {
            return investor.verificationDocuments && _.some(investor.verificationDocuments, {isValid: true, isEditing: false});
        });

        let isEditingAnyDoc = _.some(this.state.investorList, function(investor) {
            return investor.verificationDocuments && _.some(investor.verificationDocuments, {isEditing: true});
        });

        let canProceedAgree = this.state.hasAllRequirements && !isEditingAnyDoc;

        const notificationStatus = this.getNotificationStatus(canProceedAgree, addedAtLeastOneDoc);
        const footerContent = FooterUtil.getContent(this.state.product.value, this.state.footerContent);
        const dockFooterStyle = FooterUtil.adjustViewport();
        const skipOrDisagreeButton = this.getSkipOrDisagreeButton();
        const downloadData = DownloadFormsHelper.createDownloadData(this.state);
        return (
            <div className="digital-consent">
                <div className="mainContent" style={dockFooterStyle}>
                    <NavigationBar
                        product={this.state.product}
                        route={this.props.route.path}
                        moreMenuContent={this.state.moreMenuContent} backButtonUrl={this.goBackFromIDVerification}
                        showMoreMenu={this.state.showMoreMenu}
                        application={this.state.formModel}
                        clientIdentificationMode={this.state.clientIdentificationMode}
                        showDownloadMenu={this.state.showDownloadMenu}
                        accountType={this.state.accountType}
                        download={downloadData}/>
                    <SectionHeader sectionName={this.state.content.pageTitle}
                        sectionHelpText="Need help"
                        NeedHelpInfo={this.state.content.sectionHelpText} />
                    <div className="container">
                        {userIdVerification}
                        {notificationStatus}
                        <div className="digital-consent-form__declaration">
                            <p>By completing and submitting this record of verification procedure and clicking 'Agree', I declare that:</p>
                            <ul>
                                <li><span className="glyphicon glyphicon-ok"></span> I have completed an identity verification procedure in accordance with the AML/CTF Rules, in the capacity of an AFSL holder or their authorised representative; and</li>
                                <li><span className="glyphicon glyphicon-ok"></span> The identity verification procedure was completed using reliable and independent documents that were either originals or original certified copies provided by the customer; and</li>
                                <li><span className="glyphicon glyphicon-ok"></span> <span>	Any document not in English was accompanied by an English translation prepared by an accredited translator
                                    <InlineInfoButton isHidden={this.state.toggleAccreditedEnglishHelp} helpText={this.state.content.accreditedEnglishHelpText} toggleShowHideHelpText={this.toggleAccreditedEnglishHelp.bind(this) } />
                                    <InlineInfoMessage isHidden={this.state.toggleAccreditedEnglishHelp} message={this.state.content.accreditedEnglishHelpText} />
                                    <span className={ClassNames({ 'hide': !this.state.toggleAccreditedEnglishHelp }) }>.</span>
                                </span>
                                </li>
                                <div className="clearfix" />
                            </ul>
                            <div className="clearfix" />
                            <p>If you disagree, you can complete the application and verification through the paper process.</p>
                            <p>To help us continue to provide the best service, please submit a copy of certified ID or a copy of an Electronic KYC verification report through ePost.</p>
                        </div>
                    </div>
                    <div className="navbar navigationBar">
                        {skipOrDisagreeButton}
                        <button type="button" onClick={this.agreeIdVerificationForm.bind(this)} className="btn btn-success btn-large digital-consent-form__agreeBtn" 
                                        disabled={!canProceedAgree}><span className="glyphicon glyphicon-menu-right pull-right" /> Agree</button>
                    </div>
                </div>
                <Footer content={footerContent} />
            </div>);
    }
}

export default withRouter(IDVerification)


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./components/IDVerification.jsx